import { useCallback, useMemo, useState } from 'react'
import { AlertCircle, Check, Info, Loader2, Pencil, X } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import dayjs from '@dcp/shared/utils/dayjs'
import {
  Badge,
  Button,
  Card,
  CardContent,
  DataTable,
  type DataTableColumn,
  DatePicker,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@atrvd/saas-ui'

/**
 * Local sort-state shape matching the DataTable `sorting.sortState` prop
 * (data-table/types.ts SortState — `{ columnKey, direction }`). Defined
 * inline because the saas-ui barrel exports two `SortState` interfaces
 * (the hook version uses `{ key, direction }`) and the wrong one wins in
 * the public type.
 */
interface DataTableSortState {
  columnKey: string
  direction: 'asc' | 'desc' | null
}
import {
  useSageChecks,
  useSageChecksCount,
  useUpdateSageCheck,
  useSageBankAccounts,
} from '@/hooks/useSageIntegration'
import { useBankingKpi, useFacilities } from '@/hooks/useBanking'
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs'
import { KpiCards } from '@/components/banking'
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatMonthYear,
} from '@dcp/shared/utils/formatters'
import {
  OUTSTANDING_CHECK_STATES,
  effectiveCheckStatus,
  canAssignStatus,
  resolveDateCashed,
} from '@/lib/sage-utils'
import type { SageCheckRow, SageCheckSortColumn, SageBankAccountRow } from '@/lib/db-tenant'
import { DCP_CHECK_STATUSES, type DcpCheckStatus } from '@/lib/db-tenant'
import {
  ALL_STATUSES_VALUE,
  nextStatusSelection,
  toStatusSelectValue,
} from './statusFilterSelection'

/**
 * Outstanding Checks page (DCP-134 + DCP-133).
 *
 * Per the BA mapping spec, an "Outstanding Check" is one that has been Posted
 * in Sage Intacct (printed and sent — STATE='C'). Bank-clearance is tracked
 * DCP-side via the `date_cashed` column (manual entry by the AP team) until
 * automatic Plaid debit matching lands. Setting `date_cashed` flips the row's
 * Status to "Paid"; per DCP-237 the row stays in this list — the table shows
 * checks in every status (Issued, Paid, Voided). Only the Outstanding Checks
 * KPI tile remains issued-only, via the separate `sage_checks_outstanding_total`
 * RPC (see useBanking.ts), so the totals stay accurate independently of the list.
 *
 * DCP-320 adds a read-only "Reconciled in Sage" column mirroring the synced
 * `sage_cleared` / `sage_cleared_date` pair. That flag also feeds
 * `effectiveCheckStatus`, so a check reconciled in Sage now reads as "Paid"
 * WITHOUT a Date Cashed — Sage proves the bank paid it but not on which day.
 * The list itself is unchanged: it still shows every status (state='C' only).
 *
 * Built on saas-ui DataTable: server-side sort + pagination + inline edit
 * for Date Cashed. Quick filters (Date Issued range, Facility, Status) sit
 * above the table and feed into the same query parameters.
 *
 * Bank Account # and Facility columns are joined client-side via lookup
 * maps over sage_bank_accounts and facilities — the underlying tables are
 * small (per-tenant catalogs) so a full prefetch is cheaper than threading
 * a PostgREST join. The Facility column resolves from the row's denormalized
 * `facility_id` (DCP-308); the Bank Account # column still resolves via
 * `financial_entity` → sage_bank_accounts.
 */

/** Rows per page for the Outstanding Checks table. */
const PAGE_SIZE = 10

/** Display labels for the DCP status override. */
const STATUS_LABEL: Record<DcpCheckStatus, string> = {
  issued: 'Issued',
  paid: 'Paid',
  voided: 'Voided',
}

/**
 * Options of the Status quick filter: an explicit "All" first, then the
 * statuses with the same labels as the Status badge.
 */
const STATUS_FILTER_OPTIONS = [
  { value: ALL_STATUSES_VALUE, label: 'All' },
  ...DCP_CHECK_STATUSES.map((status) => ({ value: status, label: STATUS_LABEL[status] })),
]

/**
 * Header tooltip for the "Reconciled in Sage" column (DCP-320).
 *
 * The value is NOT a cash date and must not be read as one: Sage flags a check
 * reconciled when the ACCOUNTANT reconciles the bank statement, which routinely
 * happens weeks after the bank actually debited the money.
 */
const SAGE_CLEARED_TOOLTIP =
  'Reconciliation status from Sage Intacct. A check is marked reconciled when the ' +
  'bank statement is reconciled — typically weeks after the bank actually paid it.'

/**
 * Column header for "Reconciled in Sage": the label plus an info affordance
 * carrying {@link SAGE_CLEARED_TOOLTIP}.
 *
 * Mirrors DataWarningIcon's accessibility contract — `role="img"` + `tabIndex`
 * + `aria-label` on the cloned `<svg>` — because `asChild` makes the icon
 * itself the Radix trigger and an SVG is neither focusable nor named by
 * default, which would leave the explanation pointer-only (WCAG 2.1.1).
 * `TooltipPortal` comes from the saas-ui barrel (never from
 * `@radix-ui/react-tooltip` directly, which resolves a second Radix instance
 * and throws) and keeps the bubble out of the table's horizontal scroller.
 */
function SageClearedHeader() {
  return (
    <span className="inline-flex items-center gap-1">
      Reconciled in Sage
      <Tooltip>
        <TooltipTrigger asChild>
          <Info
            className="h-3.5 w-3.5 cursor-help text-muted-foreground"
            role="img"
            tabIndex={0}
            aria-label={SAGE_CLEARED_TOOLTIP}
          />
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent side="top" className="max-w-xs">
            <p>{SAGE_CLEARED_TOOLTIP}</p>
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </span>
  )
}

/**
 * Render Sage's tri-state reconciliation flag for one check (DCP-320).
 *
 *   'T' → the reconciliation period, month + year only. The DAY inside
 *         `sage_cleared_date` is a bank-statement artefact (when the statement
 *         was closed), not the day the bank paid the check, so showing it would
 *         invite reading it as a cash date — hence {@link formatMonthYear}.
 *   'M' → "Matched": auto-matched to a statement line but NOT yet reconciled;
 *         the match can still be broken, so it is not proof of payment (this is
 *         why `effectiveCheckStatus` tests `=== 'T'`, not `!== 'F'`).
 *   'F' → "In transit": Sage has seen the check and it is not on a statement.
 *   null/unknown → em-dash: never synced (rows predating DCP-320) or a code
 *         Sage added since. Deliberately indistinguishable from "no data",
 *         because that is exactly what it is.
 *
 * @param row - The check row; reads `sage_cleared` and `sage_cleared_date`.
 * @returns The cell text.
 */
function sageClearedLabel(
  row: Pick<SageCheckRow, 'sage_cleared' | 'sage_cleared_date'>,
): string {
  switch (row.sage_cleared) {
    case 'T': {
      // A cleared flag with a missing/garbled date still has to render: fall
      // back to the table's em-dash rather than formatMonthYear's plain '-'.
      const period = formatMonthYear(row.sage_cleared_date)
      return period === '-' ? '—' : period
    }
    case 'M':
      return 'Matched'
    case 'F':
      return 'In transit'
    default:
      return '—'
  }
}

/** Badge variant per effective DCP status. */
function statusBadgeVariant(
  status: DcpCheckStatus,
): 'secondary' | 'outline' | 'destructive' | 'default' {
  if (status === 'voided') return 'destructive'
  if (status === 'paid') return 'default'
  return 'outline' // issued
}

/**
 * Whether a check reads as Paid ONLY because Sage reconciled it (DCP-320): no
 * DCP override, no Date Cashed, `sage_cleared === 'T'`. Such a row opens the
 * editor as "Paid" with an empty date — the true cash day is unknown, and the
 * Sage reconciliation month-end must NOT be typed in as if it were.
 *
 * @param row - The check row being edited.
 * @returns True when Paid is derived from Sage alone.
 */
function isSageReconciledUndated(
  row: Pick<SageCheckRow, 'dcp_check_status' | 'date_cashed' | 'sage_cleared'>,
): boolean {
  return !row.dcp_check_status && !row.date_cashed && row.sage_cleared === 'T'
}

/**
 * What the row showed when its editor was opened — the draft's starting point
 * (DCP-320). Captured once in `beginEdit`; see `isDraftUnchanged` for why the
 * comparison must not use the live row.
 */
interface EditBaseline {
  status: DcpCheckStatus
  dateCashed: string | null
}

/**
 * Whether the user edited nothing since opening the editor (DCP-320). An
 * unchanged draft is a no-op: Save is never blocked for it and saving just
 * leaves edit mode WITHOUT a write. This is what lets a Sage-reconciled row
 * (seeded as "Paid" with no date) be opened and saved without forcing a Date
 * Cashed — and without the alternative of seeding "Issued", which would make a
 * plain Save write an 'issued' override and revive the check as outstanding.
 *
 * Compared against the edit-open SNAPSHOT, not the live row: a refetch while
 * the editor is open (e.g. a sync flipping sage_cleared F→T) changes the live
 * effective status, and an untouched "Issued" draft would then look like a
 * change — writing, via storedStatusFor('issued', 'T'), an 'issued' override
 * that permanently pins a check Sage just reconciled.
 *
 * @param baseline        - The row's effective status + date at edit-open time.
 * @param draftStatus     - The draft Status.
 * @param draftDateCashed - The draft Date Cashed (ISO) or null.
 * @returns True when the draft equals what the editor was opened with.
 */
function isDraftUnchanged(
  baseline: EditBaseline,
  draftStatus: DcpCheckStatus,
  draftDateCashed: string | null,
): boolean {
  return draftStatus === baseline.status && draftDateCashed === baseline.dateCashed
}

/**
 * Status cell for the Outstanding Checks table (DCP-275).
 *
 * Two modes, driven by the page-level row edit state:
 *  - Display: the effective status as a Badge plus a hover-revealed pencil
 *    (mirrors EditableBalanceCell) so it is evident the field is editable
 *    (AC#3). Clicking the pencil puts the WHOLE row — Status + Date Cashed —
 *    into edit mode, because the two fields are saved together.
 *  - Edit: a Select over every status. All options are always enabled; the
 *    cross-field rule (Paid needs a date) is enforced on Save, not by hiding
 *    options, so the user can switch between all statuses freely (AC#2).
 */
function StatusCell({
  row,
  isEditing,
  draftStatus,
  onDraftStatusChange,
  onBeginEdit,
  isPending,
}: {
  row: SageCheckRow
  isEditing: boolean
  draftStatus: DcpCheckStatus
  onDraftStatusChange: (status: DcpCheckStatus) => void
  onBeginEdit: (row: SageCheckRow) => void
  isPending: boolean
}) {
  if (isEditing) {
    return (
      <Select value={draftStatus} onValueChange={(v) => onDraftStatusChange(v as DcpCheckStatus)}>
        <SelectTrigger className="h-8 w-[100px]" aria-label="Check status">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DCP_CHECK_STATUSES.map((option) => (
            <SelectItem key={option} value={option}>
              {STATUS_LABEL[option]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  const status = effectiveCheckStatus(row)
  return (
    <div className="group flex items-center gap-1">
      <Badge variant={statusBadgeVariant(status)}>{STATUS_LABEL[status]}</Badge>
      {/* Hover-revealed edit affordance (AC#3) — opacity toggle copied from
          EditableBalanceCell so editability is discoverable without clutter. */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => onBeginEdit(row)}
        disabled={isPending}
        aria-label="Edit status and date cashed"
        className="h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>
    </div>
  )
}

/**
 * Date Cashed cell for the Outstanding Checks table (DCP-275).
 *
 * Shares the row edit mode with StatusCell and additionally owns the Save /
 * Cancel controls, because the cross-field rule lives on this field: a "Paid"
 * status requires a Date Cashed.
 *  - Display: the date is shown ONLY for Paid checks; Issued/Voided render an
 *    em-dash. Hover reveals the same pencil affordance.
 *  - Edit: the DatePicker appears ONLY when the draft status is "Paid" (Date
 *    Cashed belongs to a cashed check); any other status shows an em-dash and is
 *    not editable, and the date is cleared on save (DCP-275). When "Paid" has no
 *    date yet, the field is highlighted as required and Save is disabled (AC#2)
 *    — except for a row that is Paid only via Sage reconciliation (DCP-320):
 *    there an empty date is the row's real state, so instead of "Required" a
 *    neutral hint asks for the bank's cash date rather than the Sage one.
 */
function DateCashedCell({
  row,
  isEditing,
  draftStatus,
  draftDateCashed,
  onDraftDateCashedChange,
  onBeginEdit,
  onSave,
  onCancel,
  saveDisabled,
  isPending,
}: {
  row: SageCheckRow
  isEditing: boolean
  draftStatus: DcpCheckStatus
  draftDateCashed: string | null
  onDraftDateCashedChange: (dateCashed: string | null) => void
  onBeginEdit: (row: SageCheckRow) => void
  onSave: (row: SageCheckRow) => void
  onCancel: () => void
  saveDisabled: boolean
  isPending: boolean
}) {
  // The value shown depends on mode: the live draft while editing, the stored
  // value otherwise. Guard against an unparseable legacy value — passing an
  // Invalid Date into the saas-ui DatePicker would throw inside react-day-picker
  // and formatDate would render "Invalid Date". Treat unparseable as "no date".
  const value = isEditing ? draftDateCashed : row.date_cashed
  const selected = useMemo(() => {
    if (!value) return undefined
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? undefined : d
  }, [value])

  if (isEditing) {
    // Date Cashed is editable ONLY for 'paid' — a cashed check records WHEN it
    // cleared. 'issued'/'voided' have no Date Cashed (it is cleared on save), so
    // the picker is replaced by a non-editable em-dash (DCP-275).
    const editable = draftStatus === 'paid'
    // DCP-320: a Sage-reconciled row legitimately has no Date Cashed (Sage knows
    // the statement period, not the cash day). Its "Paid, no date" draft is the
    // unchanged state, so Save stays enabled (see isDraftUnchanged) and a hint
    // replaces "Required" — steering the user away from typing the Sage
    // reconciliation month-end, which is not the date the bank paid the check.
    const sageHint = editable && !draftDateCashed && isSageReconciledUndated(row)
    // 'paid' with no date yet is otherwise the one blocking state: highlight the
    // field and let the page-computed `saveDisabled` gate the Save button (AC#2).
    const required = editable && !draftDateCashed && !sageHint
    return (
      <div className="flex items-center gap-1">
        {editable ? (
          // The picker stays in normal flow while the "required" affordances are
          // kept out of it (DCP-289): a destructive ring (not a border) and an
          // absolutely-positioned hint add no height, so the row never grows and
          // the field never jumps out of alignment with the Status cell.
          <div className="relative">
            <div className={required ? 'rounded-md ring-1 ring-destructive' : undefined}>
              <DatePicker
                aria-label="Date Cashed"
                date={selected}
                onDateChange={(date) =>
                  onDraftDateCashedChange(date ? dayjs(date).format('YYYY-MM-DD') : null)
                }
                placeholder={required ? 'Required' : 'Pick a date'}
                // 30% narrower than the 200px default (DCP-275): the column is
                // fixed-width and only needs room for a short ISO date; the 260px
                // column reserve still fits this picker plus Save/Cancel when the
                // table overflows to its min-width (DCP-289).
                className="sm:w-[140px]"
              />
            </div>
            {required && (
              <span className="absolute left-1/2 top-full mt-0.5 -translate-x-1/2 whitespace-nowrap text-xs text-destructive">
                Required for Paid
              </span>
            )}
            {/* Same out-of-flow placement as "Required for Paid" (DCP-289), but
                left-anchored so the longer copy stays inside this column. */}
            {sageHint && (
              <span className="absolute left-0 top-full mt-0.5 whitespace-nowrap text-xs text-muted-foreground">
                Bank cash date, not Sage reconciliation
              </span>
            )}
          </div>
        ) : (
          // Non-'paid' status: Date Cashed is not applicable; it is cleared on save.
          <span className="text-muted-foreground" aria-label="Date Cashed not applicable">
            —
          </span>
        )}
        {/* Save / Cancel — mirrors EditableBalanceCell's check/x controls. */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onSave(row)}
          disabled={saveDisabled}
          aria-label="Save check"
          className="h-6 w-6"
        >
          {isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onCancel}
          disabled={isPending}
          aria-label="Cancel edit"
          className="h-6 w-6"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    )
  }

  // Date Cashed is shown only for Paid checks (DCP-275); Issued/Voided → em-dash.
  const isPaid = effectiveCheckStatus(row) === 'paid'
  const display = isPaid && selected ? formatDate(row.date_cashed as string) : '—'
  return (
    <div className="group flex items-center gap-1">
      <span className={isPaid && selected ? '' : 'text-muted-foreground'}>{display}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => onBeginEdit(row)}
        disabled={isPending}
        aria-label="Edit status and date cashed"
        className="h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>
    </div>
  )
}

/**
 * Convert a Date to an ISO date string (YYYY-MM-DD) for Postgres date filters.
 * Returns undefined when the input is undefined OR is an Invalid Date — without
 * the `isValid()` guard, `new Date('garbage')` would slip past the `if (!d)`
 * check (`!new Date('garbage')` is `false`) and dayjs.format() would return
 * the literal string `'Invalid Date'`, which Postgres would then reject as a
 * malformed timestamp.
 */
function toDateOnlyIso(d: Date | undefined): string | undefined {
  if (!d || !dayjs(d).isValid()) return undefined
  return dayjs(d).format('YYYY-MM-DD')
}

export function OutstandingChecksPage() {
  // ============ State ============

  /** 1-indexed page number — matches saas-ui Pagination contract. */
  const [page, setPage] = useState(1)

  /**
   * Server-mode sort state. Single-column for now — DataTable supports
   * multi-column with Ctrl+Click but the BA spec only asks for ASC/DESC
   * toggling on a single column at a time.
   */
  const [sortState, setSortState] = useState<DataTableSortState[]>([])

  /**
   * Quick filter: Date Issued range, filters by `when_paid`. Both bounds are
   * independently optional (unlike saas-ui's `DateRange`, where `from` is
   * required) so the From/To DatePickers can each be set or cleared on their
   * own; `undefined` overall means no date filter (all checks). DCP-238.
   */
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date } | undefined>()

  /**
   * Quick filter: tenant facility multi-select. Passed straight through to the
   * query as `facilityIds` — filtered on the denormalized
   * `sage_checks.facility_id` column (DCP-308), no bank-account resolution.
   */
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>([])

  /**
   * Quick filter: effective check status (what the Status badge shows). Empty
   * means every status — the page's default, shown as the "All" option; the
   * MultiSelect value is mapped to and from this by `statusFilterSelection.ts`.
   */
  const [selectedStatuses, setSelectedStatuses] = useState<DcpCheckStatus[]>([])

  // ============ Data ============

  // `??` not `=` because the hooks return { data: null } (not undefined)
  // during loading; the destructuring default only kicks in for undefined.
  // Wrapped in useMemo to keep array identity stable across renders so
  // downstream useMemos don't churn on every render.
  const facilitiesQuery = useFacilities()
  const facilities = useMemo(() => facilitiesQuery.data ?? [], [facilitiesQuery.data])
  const bankAccountsQuery = useSageBankAccounts({ activeOnly: false })
  const bankAccounts = useMemo(
    () => bankAccountsQuery.data ?? [],
    [bankAccountsQuery.data],
  )

  // The mapped-but-no-bank-account banner reads `bankAccounts` — a SEPARATE query
  // from the checks list. Only trust an EMPTY result once that query has actually
  // resolved: otherwise a fast/cached checks query paired with a still-loading (or
  // errored) bank-accounts query leaves bankAccounts = [], and every mapped
  // selected facility would falsely trip the banner (DCP-308 review #2).
  const bankAccountsReady = !bankAccountsQuery.isLoading && !bankAccountsQuery.isError

  /**
   * Aggregated balance tiles (DCP-274): the same KpiCards row shown on every
   * other Banking tab (Dashboard / Balance Import / Transactions Import).
   * Scoped to the page's Facility filter so the tiles track the active
   * selection. The page's Date Issued filter is intentionally NOT applied —
   * those tiles are balance figures (latest / as-of), conceptually distinct
   * from a check's issue date, so feeding the check filter into them would be
   * misleading. No filter → org-wide latest balances, matching the dashboard.
   */
  const kpiFilters = useMemo(
    () => ({ facilityIds: selectedFacilityIds.length ? selectedFacilityIds : undefined }),
    [selectedFacilityIds],
  )
  const { data: kpi, isLoading: kpiLoading } = useBankingKpi(kpiFilters)

  /**
   * Facility filter passed straight to the query (DCP-308): every selected
   * facility contributes to `sage_checks.facility_id IN (...)` regardless of
   * whether it has a synced bank account. `undefined` when nothing is selected
   * so the hook treats it as "all facilities" rather than "no rows".
   */
  const facilityIds = selectedFacilityIds.length ? selectedFacilityIds : undefined

  /**
   * Names of selected facilities that have no `sage_location_id` configured.
   *
   * Surfaced as an inline Alert above the table (see render path below) so
   * the operator can distinguish two states that otherwise look identical:
   *
   *   1. "This facility has 0 outstanding checks" — a legitimate empty
   *      result the user can act on (file the next sync, look at another
   *      facility).
   *   2. "This facility has no Sage Location ID mapping configured" — a
   *      *configuration* gap that requires an admin to fill the field in
   *      Settings → Facilities. Without this signal the user sees the
   *      same "No outstanding checks match the current filters." message
   *      and assumes the data is missing, double-working an issue that's
   *      actually on the admin side.
   *
   * Inline Alert (rather than a transient toast) is the right surface
   * because the message is *persistent* — it tells the operator the
   * filter is doing something they may not have expected, and stays
   * visible until they change the selection. A toast that auto-dismisses
   * after 4s would be missed if the user was reading the table.
   */
  const facilitiesWithoutSageMapping = useMemo(() => {
    if (selectedFacilityIds.length === 0) return []
    return facilities
      .filter((f) => selectedFacilityIds.includes(f.id) && !f.sage_location_id)
      .map((f) => f.name)
  }, [selectedFacilityIds, facilities])

  /**
   * Selected facilities that DO have a `sage_location_id` but map to ZERO
   * bank accounts (DCP-308). The mapping exists, yet no `sage_bank_accounts`
   * row carries a matching `location_id` (case-insensitive).
   *
   * Since Stage 2c the facility FILTER no longer depends on this resolution —
   * checks are filtered by `facility_id` directly (no silent no-op). But a
   * facility with no bank account has no checks ATTRIBUTED to it: `facility_id`
   * is stamped at sync time via financial_entity → bank account → location, so
   * with no bank account no check ever resolves to it. Selecting such a facility
   * therefore shows no rows until the operator runs Sync Bank Accounts (then
   * Sync Checks) for that Sage location. (Any pre-existing rows — from a bank
   * account since removed — would render "—" in the Bank Account # column.)
   * Unlike `facilitiesWithoutSageMapping` (a missing mapping the admin must
   * add), this is a data-sync gap. Surfaced as its own line so the two states
   * never read as one; each entry keeps the facility name and the mapped Sage
   * location id for an actionable message.
   */
  const facilitiesMappedWithoutBankAccount = useMemo(() => {
    if (selectedFacilityIds.length === 0) return []
    // Set of bank-account location ids, upper-cased, for a case-insensitive
    // membership test (both sides are normalized because the admin-typed
    // sage_location_id may differ in case from Sage's canonical location_id).
    const bankLocationIds = new Set(
      bankAccounts
        .filter((ba) => ba.location_id != null)
        .map((ba) => (ba.location_id as string).toUpperCase()),
    )
    return facilities
      .filter(
        (f) =>
          selectedFacilityIds.includes(f.id) &&
          f.sage_location_id &&
          !bankLocationIds.has((f.sage_location_id as string).toUpperCase()),
      )
      .map((f) => ({ id: f.id, name: f.name, sageLocationId: f.sage_location_id as string }))
  }, [selectedFacilityIds, facilities, bankAccounts])

  // Server-mode sort: pluck the active column + direction (single-column).
  // SortDirection includes `null` when the user toggles a column off; treat
  // that as "no sort" so the DB query falls back to the default order.
  const activeSort = sortState[0]
  const sortColumn =
    activeSort && activeSort.direction !== null
      ? (activeSort.columnKey as SageCheckSortColumn)
      : undefined
  const sortDirection: 'asc' | 'desc' | undefined =
    activeSort && activeSort.direction !== null ? activeSort.direction : undefined

  const dateFrom = toDateOnlyIso(dateRange?.from)
  const dateTo = toDateOnlyIso(dateRange?.to)

  const queryArgs = {
    // Pass the readonly constant directly — `useSageChecks.states` accepts
    // `readonly string[]`, so a spread-copy on every render would be pure
    // GC waste with no type-safety benefit.
    states: OUTSTANDING_CHECK_STATES,
    // DCP-237: the table intentionally shows checks in ALL statuses
    // (Issued, Paid, Voided) — `outstandingOnly` is deliberately omitted
    // so a check stays visible after Date Cashed / status edits. The
    // "Outstanding Checks" KPI tile remains issued-only via its dedicated
    // `sage_checks_outstanding_total` RPC (useBanking.ts), so the tile
    // stays accurate independently of this list.
    facilityIds,
    statuses: selectedStatuses.length ? selectedStatuses : undefined,
    dateFrom,
    dateTo,
  }

  const countQuery = useSageChecksCount(queryArgs)
  const totalCount = countQuery.data ?? 0

  // Clamp the count to non-negative finite numbers before dividing so a
  // garbage value from the RPC (NaN if the SQL ever changes shape, negative
  // from a future schema bug) can't propagate into `Math.ceil` and bubble
  // up as a NaN page count that would break pagination silently.
  const safeTotalCount = Number.isFinite(totalCount) ? Math.max(0, totalCount) : 0
  const totalPages = Math.max(1, Math.ceil(safeTotalCount / PAGE_SIZE))

  /**
   * Keep the page inside the loaded total. The total can shrink under the
   * current page without any page action (a status edit drops the last row out
   * of an active Status filter), so `page` is reconciled during render — React's
   * "adjust state while rendering" pattern, not an effect. The state itself is
   * corrected, so a later count increase cannot jump back to the stale page.
   *
   * Gated on loaded DATA, not on query status: a failed refetch keeps the last
   * count (and so `totalPages`) while `isSuccess` turns false, and a count that
   * is still loading (`undefined`) must not reset the page to 1. No loop: after
   * `setPage(totalPages)` the condition is false, and the count query's key
   * holds only the filters, never the page.
   */
  const countLoaded = countQuery.data !== undefined
  if (countLoaded && page > totalPages) {
    setPage(totalPages)
  }
  // Same clamp for this render's query args, so the render React discards
  // after the setPage above never asks for the out-of-range page.
  const currentPage = countLoaded ? Math.min(page, totalPages) : page

  const checksQuery = useSageChecks({
    page: currentPage,
    pageSize: PAGE_SIZE,
    sortColumn,
    sortDirection,
    ...queryArgs,
  })
  const rows = checksQuery.data ?? []
  const isLoading = checksQuery.isLoading
  const error = checksQuery.error
  const refetch = checksQuery.refetch

  // ============ Derived ============

  const hasActiveFilters =
    !!dateRange || selectedFacilityIds.length > 0 || selectedStatuses.length > 0

  /**
   * Lookup map: financial_entity_id → SageBankAccountRow.
   * Used to render Bank Account # column (and resolve Facility from location_id).
   */
  const bankAccountByEntity = useMemo<Map<string, SageBankAccountRow>>(() => {
    return new Map(bankAccounts.map((ba) => [ba.financial_entity_id, ba]))
  }, [bankAccounts])

  /**
   * Lookup map: facility id → facility name. DCP-308: the Facility column now
   * renders from the check row's denormalized `facility_id` directly, so this
   * keys on the facility PK instead of the old sage_location_id chain.
   */
  const facilityNameById = useMemo<Map<string, string>>(() => {
    return new Map(facilities.map((f) => [f.id, f.name]))
  }, [facilities])

  const facilityOptions = useMemo(
    () =>
      facilities
        .filter((f) => f.is_active)
        .map((f) => ({ value: f.id, label: f.name })),
    [facilities],
  )

  // ============ Mutations & inline edit state ============

  // One mutation writes both DCP-mutable columns (dcp_check_status + date_cashed)
  // atomically — see useUpdateSageCheck / sageChecks.updateCheck (DCP-275).
  const updateCheck = useUpdateSageCheck()

  /**
   * The check currently in inline-edit mode (its `record_no`), or null when no
   * row is being edited. Only one row edits at a time; the draft below holds the
   * uncommitted Status + Date Cashed until the user Saves.
   */
  const [editingRecordNo, setEditingRecordNo] = useState<number | null>(null)
  /** Draft Status while the row is being edited. */
  const [draftStatus, setDraftStatus] = useState<DcpCheckStatus>('issued')
  /** Draft Date Cashed (ISO YYYY-MM-DD) or null while the row is being edited. */
  const [draftDateCashed, setDraftDateCashed] = useState<string | null>(null)
  /**
   * Snapshot of the row's effective status + Date Cashed taken when the editor
   * opened (DCP-320) — the reference for "the user edited nothing". Null when
   * no row is being edited.
   */
  const [editBaseline, setEditBaseline] = useState<EditBaseline | null>(null)

  /**
   * Enter edit mode for a row, seeding the draft from its current effective
   * status and Date Cashed. Triggered by the pencil on EITHER the Status or the
   * Date Cashed cell — both edit the same row's two fields together.
   */
  const beginEdit = useCallback((row: SageCheckRow): void => {
    const status = effectiveCheckStatus(row)
    setEditingRecordNo(row.record_no)
    setDraftStatus(status)
    setDraftDateCashed(row.date_cashed)
    setEditBaseline({ status, dateCashed: row.date_cashed })
  }, [])

  /**
   * Whether the open editor's draft is untouched since edit-open (DCP-320).
   * Page-level, like `saveBlocked`, because only one row edits at a time.
   */
  const draftUnchanged =
    editBaseline !== null && isDraftUnchanged(editBaseline, draftStatus, draftDateCashed)

  /** Discard the draft and leave edit mode without saving. */
  const cancelEdit = useCallback((): void => {
    setEditingRecordNo(null)
  }, [])

  /**
   * Persist the draft Status + Date Cashed in one atomic write. On success the
   * optimistic cache update flips the row and we leave edit mode; on failure the
   * toast surfaces the error and the row stays in edit mode so the operator can
   * correct the input or retry. The error is caught here (not re-thrown) because
   * this is the top-level caller — nothing above needs to react to it.
   *
   * An unchanged draft (DCP-320, see isDraftUnchanged — compared against the
   * edit-open snapshot) just leaves edit mode with no write, so opening and
   * saving a row can never turn into an override, even if a refetch changed
   * the row meanwhile. The LIVE row's `sage_cleared` travels with every real
   * write: the data layer needs Sage's current view to decide whether Issued
   * is stored as an explicit override or as NULL ("follow Sage").
   */
  const saveEdit = useCallback(
    async (row: SageCheckRow): Promise<void> => {
      if (draftUnchanged) {
        setEditingRecordNo(null)
        return
      }
      try {
        await updateCheck.mutateAsync({
          recordNo: row.record_no,
          status: draftStatus,
          dateCashed: draftDateCashed,
          sageCleared: row.sage_cleared,
        })
        setEditingRecordNo(null)
      } catch (err) {
        const baseMsg = err instanceof Error ? err.message : 'Failed to update check'
        // Identifier priority mirrors the cell aria-labels: prefer the
        // user-facing Sage check number (`doc_number`), fall back to the
        // internal `record_no` for Approved-state rows that have no DocNumber yet.
        const checkLabel = row.doc_number
          ? `check ${row.doc_number}`
          : `check record ${row.record_no}`
        toast.error(`Failed to update ${checkLabel}: ${baseMsg}`)
      }
    },
    [updateCheck, draftStatus, draftDateCashed, draftUnchanged],
  )

  /**
   * Whether the current draft cannot be saved, via the shared `canAssignStatus`
   * validator the data layer also uses (so the Save gate can't drift from
   * server-side validation). The date is resolved the same way the server
   * resolves it — only 'paid' carries a date; any other status clears it — so a
   * non-Paid save always passes. Row-independent on purpose; the per-row Save
   * gate below additionally lets an unchanged draft through (DCP-320).
   */
  const saveBlocked = !canAssignStatus(
    draftStatus,
    resolveDateCashed(draftStatus, draftDateCashed),
  ).ok

  /**
   * record_no whose save is currently in flight (drives the cell spinner).
   * Pulled from the mutation's `variables` to avoid a parallel state slot.
   */
  const pendingRecordNo =
    updateCheck.isPending && updateCheck.variables ? updateCheck.variables.recordNo : null

  // ============ Columns ============

  const columns = useMemo<DataTableColumn<SageCheckRow>[]>(
    () => [
      {
        key: 'when_paid',
        header: 'Issued',
        sortable: true,
        render: (row) => {
          // Only Posted checks (STATE='C') reach this table; Sage always
          // populates WHENPAID on Posted rows, so when_paid is the sole source.
          const issuedDate = row.when_paid
          return issuedDate ? formatDate(issuedDate) : '—'
        },
      },
      {
        key: 'facility',
        header: 'Facility',
        sortable: false,
        render: (row) => {
          // DCP-308: render from the denormalized facility_id directly. Falls
          // back to '—' when the check has no facility or the id is unknown.
          if (!row.facility_id) return '—'
          return facilityNameById.get(row.facility_id) ?? '—'
        },
      },
      {
        key: 'bank_account_no',
        header: 'Bank Account #',
        // Joined column — server-side sort isn't supported (would require a
        // PostgREST join); leave unsortable for the table.
        sortable: false,
        render: (row) => {
          // Explicit null branch so an unmapped check (no `financial_entity`
          // yet — happens for Approved-state Sage rows pre-Posting) skips
          // the Map.get('') lookup entirely. Functionally equivalent to the
          // prior `.get(row.financial_entity ?? '')` chain since Map.get('')
          // returns undefined, but the explicit branch reads as the
          // operator's mental model and avoids a needless hash probe.
          if (!row.financial_entity) return '—'
          return bankAccountByEntity.get(row.financial_entity)?.bank_account_no ?? '—'
        },
      },
      {
        key: 'vendor_id',
        header: 'Vendor ID',
        sortable: true,
        render: (row) => row.vendor_id ?? '—',
      },
      // payee is sourced from the Sage VENDOR "Print To" field
      // (DISPLAYCONTACT.PRINTAS), resolved during the check sync via Payee
      // enrichment (fetchVendorPrintAsMap in sage/sync-checks). vendor_name is
      // kept as a fallback for rows that were synced before the payee column
      // was added and have not yet been re-synced.
      {
        key: 'payee',
        header: 'Payee',
        sortable: true,
        render: (row) => row.payee ?? row.vendor_name ?? '—',
      },
      {
        key: 'doc_number',
        header: 'Check #',
        sortable: true,
        // Row identity column — must always stay visible (DCP-279).
        disableHide: true,
        render: (row) => row.doc_number ?? '—',
      },
      {
        key: 'total_paid',
        header: 'Amount',
        sortable: true,
        // Left-aligned to match the (left-aligned) column headers — every column
        // on this table reads left-to-right, including the currency amount.
        cellClassName: 'font-medium',
        render: (row) => {
          // Only Posted checks (STATE='C') reach this table; Sage always
          // populates TOTALPAID on Posted rows, so total_paid is the sole source.
          const amount = Number(row.total_paid) || 0
          return formatCurrency(amount)
        },
      },
      {
        key: 'state',
        header: 'Status',
        sortable: true,
        render: (row) => (
          <StatusCell
            row={row}
            isEditing={editingRecordNo === row.record_no}
            draftStatus={draftStatus}
            onDraftStatusChange={setDraftStatus}
            onBeginEdit={beginEdit}
            isPending={pendingRecordNo === row.record_no}
          />
        ),
      },
      {
        key: 'date_cashed',
        header: 'Date Cashed',
        sortable: true,
        // Fixed width so the column does not jump between display (date + pencil)
        // and edit (DatePicker + Save/Cancel) modes — the wider edit layout sets
        // the reserved width (DCP-275). 260px gives the 140px picker plus the two
        // icon buttons breathing room so the edit controls are not crammed against
        // the table's right edge (DCP-289).
        width: '260px',
        minWidth: '260px',
        // Custom render: shares the row-level edit mode with the Status column
        // and owns the Save/Cancel controls (DCP-275). Both fields are edited
        // and saved together via a single atomic mutation.
        render: (row) => (
          <DateCashedCell
            row={row}
            isEditing={editingRecordNo === row.record_no}
            draftStatus={draftStatus}
            draftDateCashed={draftDateCashed}
            onDraftDateCashedChange={setDraftDateCashed}
            onBeginEdit={beginEdit}
            onSave={saveEdit}
            onCancel={cancelEdit}
            // An unchanged draft is a no-op save, so it is never blocked —
            // otherwise a Sage-reconciled row (Paid, no date) could not even be
            // saved back as-is (DCP-320).
            saveDisabled={
              (saveBlocked && !draftUnchanged) ||
              pendingRecordNo === row.record_no
            }
            isPending={pendingRecordNo === row.record_no}
          />
        ),
      },
      {
        key: 'sage_cleared',
        // ReactNode header (label + info icon); `headerLabel` supplies the plain
        // text the column-visibility menu needs.
        header: <SageClearedHeader />,
        headerLabel: 'Reconciled in Sage',
        // Read-only mirror of Sage — no DCP override exists for this field.
        // Not sortable: `sage_cleared` is absent from SAGE_CHECK_SORT_COLUMNS,
        // and sorting is server-side, so making it sortable is a data-layer
        // change (allow-list + a composite ordering on the date), not free.
        sortable: false,
        // DCP-320 ships this column, so it must arrive VISIBLE for users who
        // already have a saved layout — `reconcile` hides newly-added columns
        // by default and every returning user has a saved blob. Still hideable
        // from the column menu; the opt-in only governs its first appearance.
        defaultVisible: true,
        render: (row) => sageClearedLabel(row),
      },
      {
        key: 'synced_at',
        header: 'Import Timestamp',
        sortable: true,
        render: (row) => (row.synced_at ? formatDateTime(row.synced_at) : '—'),
      },
    ],
    [
      bankAccountByEntity,
      facilityNameById,
      editingRecordNo,
      draftStatus,
      draftDateCashed,
      beginEdit,
      saveEdit,
      cancelEdit,
      saveBlocked,
      draftUnchanged,
      pendingRecordNo,
    ],
  )

  // DCP-279: per-user column visibility + order, persisted in localStorage.
  const columnPrefs = useTableColumnPrefs('outstanding-checks', columns)

  // ============ Render ============

  /** Reset all quick filters and the page index. */
  const clearFilters = () => {
    setDateRange(undefined)
    setSelectedFacilityIds([])
    setSelectedStatuses([])
    setPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Outstanding Checks</h1>
        <p className="text-muted-foreground">
          Posted AP checks from Sage Intacct, shown in every status — Issued, Paid and Voided.
        </p>
      </div>

      {/* Aggregated balance tiles (DCP-274) — parity with the other Banking
          tabs. Rendered at the top of the page per the bug's AC#1. */}
      <KpiCards kpi={kpi} loading={kpiLoading} />

      {/* Quick filters: Date Issued + Facility (DCP-133 AC#8) + Status */}
      <div className="flex flex-wrap items-end gap-3">
        {/*
         * Date Issued is split into two independent, clearable DatePickers
         * (From / To) — same pattern as the Banking Dashboard's BankingFilters
         * (DCP-238). The previous single DateRangePicker visually pre-filled
         * the last 30 days and had no clear path. Each picker is optional: an
         * empty From/To simply omits that bound from the query, and clearing
         * both collapses dateRange back to undefined (no date filter → all
         * checks). A light clamp keeps From <= To.
         */}
        <div className="flex flex-col gap-1">
          <label id="oc-from-date-label" className="text-sm mb-1 font-medium">
            Date Issued — From
          </label>
          <DatePicker
            aria-labelledby="oc-from-date-label"
            date={dateRange?.from}
            onDateChange={(date) => {
              setPage(1)
              setDateRange((prev) => {
                if (!date) {
                  // Clearing From: keep To if set, else drop the filter entirely.
                  return prev?.to ? { from: undefined, to: prev.to } : undefined
                }
                // If From moves past To, pull To up to match.
                const to = prev?.to && date.getTime() > prev.to.getTime() ? date : prev?.to
                return { from: date, to }
              })
            }}
            placeholder="From"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label id="oc-to-date-label" className="text-sm mb-1 font-medium">
            Date Issued — To
          </label>
          <DatePicker
            aria-labelledby="oc-to-date-label"
            date={dateRange?.to}
            onDateChange={(date) => {
              setPage(1)
              setDateRange((prev) => {
                if (!date) {
                  // Clearing To: keep From if set, else drop the filter entirely.
                  return prev?.from ? { from: prev.from, to: undefined } : undefined
                }
                // If To moves before From, pull From down to match.
                const from = prev?.from && date.getTime() < prev.from.getTime() ? date : prev?.from
                return { from, to: date }
              })
            }}
            placeholder="To"
          />
        </div>
        <div className="min-w-[240px]">
          <MultiSelect
            label="Facility"
            options={facilityOptions}
            value={selectedFacilityIds}
            onChange={(values) => {
              setSelectedFacilityIds(values)
              setPage(1)
            }}
            placeholder="All facilities"
            searchable
            searchPlaceholder="Search facilities..."
          />
        </div>
        <div className="min-w-[200px]">
          <MultiSelect
            label="Status"
            options={STATUS_FILTER_OPTIONS}
            value={toStatusSelectValue(selectedStatuses)}
            onChange={(values) => {
              setSelectedStatuses((prev) => nextStatusSelection(prev, values))
              setPage(1)
            }}
            placeholder="All"
          />
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="mb-0.5">
            <X className="mr-1 h-4 w-4" /> Clear filters
          </Button>
        )}
      </div>

      {/* Missing-Sage-mapping warning.
          Surfaced when the active facility filter includes one or more
          facilities with no `sage_location_id`. Without this banner the
          page would show "No outstanding checks match the current filters."
          and the user would assume the data is missing — see the comment
          on `facilitiesWithoutSageMapping` for the configuration-vs-data
          distinction this preserves. Uses the warning (amber) palette so
          it's visually distinct from the destructive (red) error banner
          below and from the muted empty-state copy. */}
      {facilitiesWithoutSageMapping.length > 0 && !isLoading && (
        <div
          role="status"
          className="flex flex-col gap-1 rounded-md border border-amber-500/50 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>
              <strong>
                {facilitiesWithoutSageMapping.length === 1
                  ? `${facilitiesWithoutSageMapping[0]} has`
                  : `${facilitiesWithoutSageMapping.length} selected facilities have`}{' '}
                no Sage Location ID configured
              </strong>{' '}
              — their checks won't appear in this list. Configure the
              mapping in <em>Settings → Facilities</em>{' '}
              {facilitiesWithoutSageMapping.length > 1 && (
                <span className="block mt-1 text-xs opacity-90">
                  Affected facilities: {facilitiesWithoutSageMapping.join(', ')}
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Mapped-but-no-bank-account warning (DCP-308).
          Distinct from the missing-mapping banner above: these facilities DO
          have a Sage Location ID, but no bank account resolves for it
          (case-insensitive), so no checks are attributed to them yet (a check
          gets its facility_id via the bank account at sync time). The fix is a
          data sync, not a config edit — the copy points the operator at
          "Sync Bank Accounts" for the named location. */}
      {facilitiesMappedWithoutBankAccount.length > 0 && !isLoading && bankAccountsReady && (
        <div
          role="status"
          className="flex flex-col gap-1 rounded-md border border-amber-500/50 bg-amber-50 p-4 text-sm text-amber-900"
        >
          {facilitiesMappedWithoutBankAccount.map((f) => (
            <div key={f.id} className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                Facility <strong>&quot;{f.name}&quot;</strong> is mapped to Sage location{' '}
                <strong>&quot;{f.sageLocationId}&quot;</strong> but no bank account was found for it
                — its checks aren&apos;t synced yet. Run <em>Sync Bank Accounts</em> (then{' '}
                <em>Sync Checks</em>) for that location to pull them.
              </p>
            </div>
          ))}
        </div>
      )}

      {/*
        Live region for the table content. `aria-live="polite"`
        announces row changes to assistive tech without interrupting
        whatever the user was reading (vs `assertive`), and
        `aria-busy={isLoading}` suppresses announcements while the
        fetch is in flight so SR users don't hear a stale row count.
        The wrapper spans loading / error / data states so a
        transition between them is announced as a single coherent
        update rather than three separate ones.
      */}
      <div aria-live="polite" aria-busy={isLoading}>
      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <div className="flex flex-col items-center gap-3 rounded-md border border-destructive/50 bg-destructive/10 p-6 text-destructive">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            <p className="font-medium">{error.message}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      )}

      {/* Data table.
          min-h-[680px] is a shared FLOOR, not a target height: the empty
          state and the populated table use the same minimum so swapping
          between them while filtering doesn't snap the layout, and it lines
          up with the resting height of the other paginated /banking/* tables
          (BankTransactionsTable, ManualTransactionsTable, ProjectionsTable),
          which run ~56px rows (≈ header 56 + 10×56 + pagination 56).
          NOTE: this table INTENTIONALLY departs from that per-row metric —
          its rows are a fixed 80px (h-20, see rowClassName below) because
          every row carries inline Status + Date Cashed edit controls that
          must reserve a stable height (DCP-289). A full 10-row page here is
          therefore taller (~912px); 680px stays the floor for the empty /
          short-page case. None of the three sibling tables has an inline row
          editor, but since DCP-361 two of them (BankTransactionsTable and
          ManualTransactionsTable) have VARIABLE row height because their
          Description cell wraps over several lines, so the ~56px figure above
          describes only their single-line resting case. Either way the
          divergence is expected and there is nothing to "keep in sync" on row
          height. */}
      {!isLoading && !error && (
        <Card>
          <CardContent className="p-0">
            {totalCount === 0 ? (
              <div className="flex min-h-[680px] items-center justify-center">
                <p className="text-sm text-muted-foreground">
                  {hasActiveFilters
                    ? 'No checks match the current filters.'
                    : 'No checks. Run Sync Checks from Settings → Sage Intacct.'}
                </p>
              </div>
            ) : (
              <DataTable<SageCheckRow>
                className="min-h-[680px]"
                title="Outstanding Checks"
                columns={columns}
                data={rows}
                // Fixed 80px row height applied to EVERY row by design (not
                // just the row being edited) so all rows read at a consistent
                // size in both display mode (date + pencil) and edit mode
                // (DatePicker + Save/Cancel + the "Required for Paid" hint).
                // A per-row conditional height would grow only the editing row
                // and reintroduce the vertical shift DCP-289 removed; a uniform
                // 80px keeps the whole table stable. This is why rows here are
                // taller than the 56px used by the sibling /banking/* tables —
                // see the min-height note above.
                rowClassName="h-20"
                rowKey={(row) => row.record_no}
                sorting={{
                  mode: 'server',
                  sortState,
                  onSortChange: (next) => {
                    setSortState(next)
                    setPage(1)
                  },
                }}
                pagination={{
                  currentPage,
                  totalPages,
                  onPageChange: setPage,
                  pageSize: PAGE_SIZE,
                  totalItems: totalCount,
                }}
                itemName="checks"
                enableColumnReorder
                {...columnPrefs}
              />
            )}
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  )
}
